import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Ellipsis,
  Info,
  Link,
  MessageSquare,
  Star,
  Tags,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import MediaCarousel from "../components/MediaCarousel";
import MediaComments from "../components/MediaComments";
import MediaInfo from "../components/MediaInfo";
import SheetShell from "../components/SheetShell";
import TagPicker from "../components/TagPicker";
import { signMediaUrls } from "../lib/api";
import { useAuth } from "../lib/auth";
import { colorOfUser } from "../lib/color";
import { formatDateTimeJa } from "../lib/dates";
import { useConfirm } from "../lib/confirm";
import { fileNameOf } from "../lib/media/archive";
import { PREFETCH_DISTANCE, usePrefetchNeighbours } from "../lib/media/detailUrls";
import {
  addFavorite,
  fetchFavoriteIds,
  fetchProtectedIds,
  removeFavorite,
} from "../lib/media/favorites";
import { saveBlob } from "../lib/media/export";
import { fetchComments } from "../lib/media/comments";
import { formatDuration } from "../lib/media/format";
import { fetchMembers } from "../lib/media/members";
import { RETENTION_DAYS } from "../lib/media/retention";
import { stepIndex, type SwipeDirection } from "../lib/media/swipe";
import { softDeleteMedia } from "../lib/media/tags";
import type { TimelineItem } from "../lib/media/timeline";
import { fetchCoupleProfiles } from "../lib/profileItems";
import { queryKeys } from "../lib/queryKeys";
import { useToast } from "../lib/toast";
import { useCloseOnBack } from "../lib/useCloseOnBack";
import { useScrollLock } from "../lib/useScrollLock";
import { PC_SCREEN_QUERY, useMediaQuery, WIDE_SCREEN_QUERY } from "../lib/useMediaQuery";

interface Props {
  items: TimelineItem[];
  /**
   * 表示中メディアの id。添字ではなく id で追う。
   * 一覧が取り直されて並びが変わっても、見ているものが黙って入れ替わらない。
   */
  currentId: string;
  onChange: (id: string) => void;
  onClose: () => void;
  /** 一覧側で署名済みのサムネイルURL。動画のポスターに流用する */
  thumbnailUrls?: Record<string, string>;
  /** 末尾に近づいたときに呼ぶ。無限スクロールの一覧が次のページを足す */
  onReachEnd?: () => void;
  /** 絞り込み後の総件数。読み込み済み件数ではなく全体を出す。未取得の間は代用する */
  total?: number;
  /** 読み込み開始位置より新しい側にある件数。月ジャンプ時に位置を全体基準へ直す */
  indexOffset?: number;
  /** 開いた直後からコメント欄を出す(コメント通知から来たとき) */
  initialSheetOpen?: boolean;
}

/** マウスが止まってから操作ボタンを引っ込めるまでの時間 */
const CHROME_HIDE_MS = 2500;

/** 入力欄でのキー操作(カーソル移動や Escape)を、送りや閉じる操作として拾わない */
function isTyping(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
  );
}

/** 写真か動画か。確認・トーストの主語に使う */
function kindOf(item: TimelineItem): string {
  return item.type === "video" ? "動画" : "写真";
}

/** コメントを下から引き出すシート(スマホ・タブレット)。PC では右のパネルで出す */
function CommentSheet({ mediaId, onClose }: { mediaId: string; onClose: () => void }) {
  useCloseOnBack(onClose);
  return (
    <div className="absolute inset-0 z-20 flex flex-col justify-end bg-black/40">
      <button type="button" aria-label="閉じる" className="flex-1" onClick={onClose} />
      <div className="flex max-h-[70vh] min-h-64 flex-col rounded-t-2xl bg-neutral-900">
        <MediaComments mediaId={mediaId} />
      </div>
    </div>
  );
}

/**
 * スマホの狭い画面でヘッダーに8個並べると閉じるボタン(X)が画面端で見切れるため、
 * 使用頻度の低い操作をここへ逃がす(削除・お気に入り・コメント・閉じるは常設のまま)
 */
function MoreActionsSheet({
  onClose,
  onInfo,
  onTags,
  onDownload,
  onShare,
  downloading,
}: {
  onClose: () => void;
  onInfo: () => void;
  onTags: () => void;
  onDownload: () => void;
  onShare: () => void;
  downloading: boolean;
}) {
  const rowClass =
    "flex items-center gap-3 border-b border-[var(--color-line)] px-4 py-3 text-left text-sm transition-colors last:border-b-0 hover:bg-[color-mix(in_srgb,var(--color-ink)_5%,transparent)] disabled:opacity-50";
  return (
    <SheetShell title="その他の操作" onClose={onClose}>
      <button type="button" onClick={onInfo} className={rowClass}>
        <Info className="size-4 text-[var(--color-sub)]" aria-hidden />
        情報
      </button>
      <button type="button" onClick={onTags} className={rowClass}>
        <Tags className="size-4 text-[var(--color-sub)]" aria-hidden />
        タグ
      </button>
      <button type="button" onClick={onDownload} disabled={downloading} className={rowClass}>
        <Download className="size-4 text-[var(--color-sub)]" aria-hidden />
        {downloading ? "ダウンロード中..." : "ダウンロード"}
      </button>
      <button type="button" onClick={onShare} className={rowClass}>
        <Link className="size-4 text-[var(--color-sub)]" aria-hidden />
        共有リンクをコピー
      </button>
    </SheetShell>
  );
}

export default function MediaDetail({
  items,
  currentId,
  onChange,
  onClose,
  thumbnailUrls,
  onReachEnd,
  total,
  indexOffset = 0,
  initialSheetOpen = false,
}: Props) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  // 拡大中は送らない。寄せて見ている最中に隣へ飛ぶのは事故にしかならない
  const [zoomed, setZoomed] = useState(false);
  // メディアを主役にする。バーはタッチではタップで、PC ではマウスの動きで出し入れする
  const [chrome, setChrome] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(initialSheetOpen);
  const [panelOpen, setPanelOpen] = useState(initialSheetOpen);
  const [tagOpen, setTagOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  // スマホはヘッダーに全部を並べると閉じるボタンが見切れるので、一部を「その他」に逃がす
  const [moreOpen, setMoreOpen] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const wide = useMediaQuery(WIDE_SCREEN_QUERY);
  // 送りボタンはマウス前提の PC だけ。タッチのスマホ・タブレットはスワイプで送る
  const pc = useMediaQuery(PC_SCREEN_QUERY);

  useCloseOnBack(onClose);
  useScrollLock();

  const index = items.findIndex((item) => item.id === currentId);
  const media = index === -1 ? undefined : items[index];

  usePrefetchNeighbours(items, index);

  const { session } = useAuth();
  const members = useQuery({ queryKey: queryKeys.members, queryFn: fetchMembers });
  const profiles = useQuery({ queryKey: queryKeys.coupleProfiles, queryFn: fetchCoupleProfiles });
  const favorites = useQuery({ queryKey: queryKeys.favorites, queryFn: fetchFavoriteIds });
  // 吹き出しの塗り分け用。コメント欄と同じキーなので、開いたときの取得と共有される
  const comments = useQuery({
    queryKey: queryKeys.comments(media?.id),
    queryFn: () => fetchComments(media?.id ?? ""),
    enabled: media !== undefined,
  });
  const commentCount = comments.data?.length ?? 0;
  const hasComments = commentCount > 0;
  const [downloading, setDownloading] = useState(false);

  // PC のバーはマウスが動いたら出し、止まったら引っ込める。
  // ボタンやコメント欄にカーソルがある間は、押そうとしている最中なので消さない
  const hideTimer = useRef<number | null>(null);
  const pinnedRef = useRef(false);
  const wake = useCallback(() => {
    setChrome(true);
    if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      if (!pinnedRef.current) setChrome(false);
    }, CHROME_HIDE_MS);
  }, []);
  const pin = useCallback(() => {
    pinnedRef.current = true;
  }, []);
  const unpin = useCallback(() => {
    pinnedRef.current = false;
    wake();
  }, [wake]);
  useEffect(() => {
    if (wide) wake();
    return () => {
      if (hideTimer.current !== null) window.clearTimeout(hideTimer.current);
    };
  }, [wide, wake]);

  /** 原本を1件保存する。署名URLを取り、実体を落として保存ダイアログへ */
  async function downloadCurrent() {
    if (media === undefined || downloading) return;
    setDownloading(true);
    try {
      const urls = await signMediaUrls([media.storage_key]);
      const url = urls[media.storage_key];
      if (url === undefined) throw new Error("署名URLがありません");
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${String(response.status)}`);
      saveBlob(await response.blob(), fileNameOf(media));
    } catch (e) {
      console.error("ダウンロードに失敗", e);
      toast("ダウンロードに失敗しました。", "error");
    } finally {
      setDownloading(false);
    }
  }

  /** 共有用リンク。開くと当人たち(サインイン済み)だけが対象の詳細に飛べる */
  async function copyShareLink() {
    if (media === undefined) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/?media=${media.id}`);
      toast("共有用リンクをコピーしました。");
    } catch (e) {
      console.error("共有リンクのコピーに失敗", e);
      toast("共有用リンクのコピーに失敗しました。", "error");
    }
  }

  const step = (direction: SwipeDirection) => {
    const next = items[stepIndex(index, direction, items.length)];
    if (next !== undefined) onChange(next.id);
  };

  // 表示中のものが一覧から消えたら閉じる(絞り込み中にお気に入りを外したときなど)。
  // 添字のままだと隣のメディアが黙って表示されてしまう
  useEffect(() => {
    if (items.length > 0 && index === -1) onClose();
  }, [index, items.length, onClose]);

  // 先読みが尽きる前に次のページを頼む。詳細を開いたまま送り続けても止まらない
  useEffect(() => {
    if (onReachEnd === undefined || index === -1) return;
    if (index >= items.length - 1 - PREFETCH_DISTANCE) onReachEnd();
  }, [index, items.length, onReachEnd]);

  // 拡大を始めたらバーを引っ込める。寄せて見たいときに重なり物は要らない
  useEffect(() => {
    if (zoomed) setChrome(false);
  }, [zoomed]);

  // 送ったら再生状態は持ち越さない(動画要素は作り直される)
  useEffect(() => {
    setVideoPlaying(false);
  }, [currentId]);

  const toggleFavorite = useMutation({
    mutationFn: async ({ id, on }: { id: string; on: boolean }) => {
      if (on) await addFavorite(id);
      else await removeFavorite(id);
    },
    onSuccess: async (_, { on }) => {
      toast(on ? "お気に入りに追加しました。" : "お気に入りを解除しました。");
      await queryClient.invalidateQueries({ queryKey: queryKeys.favorites });
      // お気に入りで絞り込んでいると、外した時点で一覧から消える
      await queryClient.invalidateQueries({ queryKey: queryKeys.timelines });
    },
    onError: (e, { on }) => {
      // 詳細はユーザーに出さない。原因はコンソールで追う
      console.error("お気に入りの切り替えに失敗", e);
      toast(on ? "お気に入りの追加に失敗しました。" : "お気に入りの解除に失敗しました。", "error");
    },
  });

  const remove = useMutation({
    mutationFn: async (target: TimelineItem) => {
      await softDeleteMedia([target.id]);
    },
    onSuccess: async (_, target) => {
      onClose();
      await queryClient.invalidateQueries({ queryKey: queryKeys.timelines });
      await queryClient.invalidateQueries({ queryKey: queryKeys.monthCounts });
      toast(`${kindOf(target)}を削除しました。`);
    },
    onError: (e, target) => {
      console.error("削除に失敗", e);
      toast(`${kindOf(target)}の削除に失敗しました。`, "error");
    },
  });

  async function askRemove() {
    if (media === undefined) return;
    const target = media;
    // お気に入りに入っているものは消さない。相手が入れたものも含めて守る
    try {
      const protectedIds = await fetchProtectedIds([target.id]);
      if (protectedIds.size > 0) {
        toast("お気に入りに追加されているため、削除できません。", "error");
        return;
      }
    } catch (e) {
      console.error("お気に入り保護の確認に失敗", e);
      toast(`${kindOf(target)}の削除に失敗しました。`, "error");
      return;
    }
    const ok = await confirm({
      message: `${kindOf(target)}を削除します。\n※${String(RETENTION_DAYS)}日以内ならゴミ箱から復元できます。`,
      confirmLabel: "削除",
      tone: "danger",
    });
    if (ok) remove.mutate(target);
  }

  // 左右キーでも送れるようにする(PCでの閲覧用)。
  // Escape は capture で先に受けて止める。下に開いているオーバーレイ
  // (お気に入りなど)まで一緒に閉じないため。タグやコメントが開いていれば
  // 一番上のそれだけを閉じる
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTyping(event.target)) return;
      if (event.key === "Escape") {
        event.stopPropagation();
        if (infoOpen) setInfoOpen(false);
        else if (tagOpen) setTagOpen(false);
        else if (sheetOpen) setSheetOpen(false);
        else if (moreOpen) setMoreOpen(false);
        else onClose();
      }
      if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
        if (wide) wake();
        const direction = event.key === "ArrowRight" ? "next" : "previous";
        const next = items[stepIndex(index, direction, items.length)];
        if (next !== undefined) onChange(next.id);
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => {
      window.removeEventListener("keydown", onKey, { capture: true });
    };
  }, [index, items, onClose, onChange, infoOpen, tagOpen, sheetOpen, moreOpen, wide, wake]);

  if (media === undefined) return null;

  const uploader = members.data?.get(media.uploader_id);
  const uploaderColor = colorOfUser(profiles.data, media.uploader_id, session?.user.id);
  const favorited = favorites.data?.has(media.id) ?? false;
  const chromeClass = chrome ? "" : "pointer-events-none opacity-0";
  const iconButton =
    "flex size-11 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-white/15 disabled:opacity-50";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="写真・動画の詳細"
      // カーソルは動画を観ている間だけ、バーと一緒に隠す(写真では隠さない)
      className={`fixed inset-0 z-50 flex bg-black text-white ${
        wide && videoPlaying && !chrome ? "cursor-none" : ""
      }`}
      onMouseMove={wide ? wake : undefined}
    >
      {/* ステージ。メディアが全面に出て、バーはその上に重なる */}
      <div className="relative min-w-0 flex-1 overflow-hidden">
        <MediaCarousel
          items={items}
          index={index}
          zoomed={zoomed}
          onZoomedChange={setZoomed}
          onTap={() => {
            // PC はマウスの動きで出し入れするので、クリックでは切り替えない
            if (!wide) setChrome((current) => !current);
          }}
          onNavigate={step}
          thumbnailUrls={thumbnailUrls}
          onVideoPlayingChange={setVideoPlaying}
        />

        <header
          onMouseEnter={wide ? pin : undefined}
          onMouseLeave={wide ? unpin : undefined}
          className={`absolute inset-x-0 top-0 z-10 flex items-center bg-gradient-to-b from-black/60 to-transparent p-3 pt-[calc(0.75rem+env(safe-area-inset-top))] transition-opacity ${chromeClass}`}
        >
          {/* 件数は wide ではステージ(コンテンツ)の中央上に絶対配置(ボタン列の幅に
              押されない)。スマホは左上にそのまま置く */}
          <span
            className={`text-xs text-white/70 ${
              wide ? "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" : ""
            }`}
          >
            {indexOffset + index + 1} / {total ?? items.length}
          </span>
          <div className="ml-auto flex items-center">
            <button
              type="button"
              onClick={() => {
                void askRemove();
              }}
              aria-label="削除"
              className={iconButton}
            >
              <Trash2 className="size-5" aria-hidden />
            </button>
            {wide && (
              <button
                type="button"
                onClick={() => {
                  setInfoOpen(true);
                }}
                aria-label="情報"
                className={iconButton}
              >
                <Info className="size-5" aria-hidden />
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                toggleFavorite.mutate({ id: media.id, on: !favorited });
              }}
              aria-pressed={favorited}
              aria-label={favorited ? "お気に入りから外す" : "お気に入りに入れる"}
              className={iconButton}
            >
              <Star
                className={`size-5 ${favorited ? "fill-[var(--color-accent)] text-[var(--color-accent)]" : ""}`}
                aria-hidden
              />
            </button>
            <button
              type="button"
              onClick={() => {
                if (wide) setPanelOpen((current) => !current);
                else setSheetOpen(true);
              }}
              aria-pressed={wide ? panelOpen : sheetOpen}
              aria-label={commentCount > 0 ? `コメント ${String(commentCount)}件` : "コメント"}
              className={`${iconButton} relative ${wide && panelOpen ? "bg-white/15" : ""}`}
            >
              <MessageSquare
                className={`size-5 ${hasComments ? "fill-[var(--color-accent)] text-[var(--color-accent)]" : ""}`}
                aria-hidden
              />
              {commentCount > 0 && (
                // 白地にテーマカラーの縁と数字。塗った吹き出しと色が揃いつつ、
                // 下地が白なので数字が沈まない。bg-white はダークで card 色に
                // 読み替えられるため、ここは白を直に置く
                <span className="absolute top-0.5 right-0.5 flex min-w-4 items-center justify-center rounded-full border-[1.5px] border-[var(--color-accent)] bg-[#fff] px-1 text-[10px] leading-4 font-bold text-[var(--color-accent)]">
                  {commentCount > 99 ? "99+" : commentCount}
                </span>
              )}
            </button>
            {wide && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setTagOpen(true);
                  }}
                  aria-label="タグ"
                  className={iconButton}
                >
                  <Tags className="size-5" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => void downloadCurrent()}
                  disabled={downloading}
                  aria-label="ダウンロード"
                  className={iconButton}
                >
                  <Download className="size-5" aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => void copyShareLink()}
                  aria-label="共有リンクをコピー"
                  className={iconButton}
                >
                  <Link className="size-5" aria-hidden />
                </button>
              </>
            )}
            {!wide && (
              // 情報・タグ・ダウンロード・共有はここへまとめる。8個並べると
              // 狭い画面で閉じるボタンが画面端で見切れるため
              <button
                type="button"
                onClick={() => {
                  setMoreOpen(true);
                }}
                aria-label="その他の操作"
                className={iconButton}
              >
                <Ellipsis className="size-5" aria-hidden />
              </button>
            )}
            <button type="button" onClick={onClose} aria-label="閉じる" className={iconButton}>
              <X className="size-5" aria-hidden />
            </button>
          </div>
        </header>

        {/* PC の送りは画面の左右・縦中央。ライトボックスの定石の位置に大きめで置く */}
        {pc && (
          <>
            <button
              type="button"
              onClick={() => {
                step("previous");
              }}
              disabled={index === 0}
              aria-label="前へ"
              onMouseEnter={pin}
              onMouseLeave={unpin}
              className={`absolute top-1/2 left-4 z-10 flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 transition-opacity hover:bg-white/15 disabled:opacity-30 ${chromeClass}`}
            >
              <ChevronLeft className="size-8" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => {
                step("next");
              }}
              disabled={index === items.length - 1}
              aria-label="次へ"
              onMouseEnter={pin}
              onMouseLeave={unpin}
              className={`absolute top-1/2 right-4 z-10 flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 transition-opacity hover:bg-white/15 disabled:opacity-30 ${chromeClass}`}
            >
              <ChevronRight className="size-8" aria-hidden />
            </button>
          </>
        )}

        <div
          onMouseEnter={wide ? pin : undefined}
          onMouseLeave={wide ? unpin : undefined}
          className={`absolute inset-x-0 bottom-0 z-10 flex items-center justify-center bg-gradient-to-t from-black/60 to-transparent px-3 pt-8 pb-[calc(0.75rem+env(safe-area-inset-bottom))] transition-opacity ${chromeClass}`}
        >
          <span className="flex flex-col items-center text-xs text-white/60">
            <span>
              {formatDateTimeJa(media.taken_at)}
              {media.duration_seconds !== null && ` ・ ${formatDuration(media.duration_seconds)}`}
            </span>
            {uploader !== undefined && (
              <span className="text-[11px]">
                <span style={{ color: uploaderColor }}>{uploader}</span>が追加
              </span>
            )}
          </span>
        </div>
      </div>

      {/* PC: コメントは右のパネル。ボタンで開閉し、送りをまたいでも開いたまま */}
      {wide && panelOpen && (
        <aside
          onMouseEnter={pin}
          onMouseLeave={unpin}
          className="flex w-96 shrink-0 flex-col border-l border-white/15"
        >
          <MediaComments mediaId={media.id} />
        </aside>
      )}

      {!wide && sheetOpen && (
        <CommentSheet
          mediaId={media.id}
          onClose={() => {
            setSheetOpen(false);
          }}
        />
      )}

      {tagOpen && (
        <TagPicker
          mediaIds={[media.id]}
          onClose={() => {
            setTagOpen(false);
          }}
        />
      )}

      {infoOpen && (
        <MediaInfo
          mediaId={media.id}
          onClose={() => {
            setInfoOpen(false);
          }}
        />
      )}

      {moreOpen && (
        <MoreActionsSheet
          downloading={downloading}
          onClose={() => {
            setMoreOpen(false);
          }}
          onInfo={() => {
            setMoreOpen(false);
            setInfoOpen(true);
          }}
          onTags={() => {
            setMoreOpen(false);
            setTagOpen(true);
          }}
          onDownload={() => {
            setMoreOpen(false);
            void downloadCurrent();
          }}
          onShare={() => {
            setMoreOpen(false);
            void copyShareLink();
          }}
        />
      )}
    </div>
  );
}
